import type { Character } from "../types/character";
import type { Zone } from "../types/zone";

export interface ZoneResolution {
  readonly zone: Zone;
  readonly winner: Character | null;
  readonly moveDistance: number;
  readonly coinCounts: Readonly<Record<string, number>>;
}

function countCoins(
  coins: readonly Character[],
): Readonly<Record<string, number>> {
  const counts: Record<string, number> = {};
  for (const coin of coins) {
    counts[coin] = (counts[coin] ?? 0) + 1;
  }
  return counts;
}

function findZoneWinner(coins: readonly Character[]): Character | null {
  if (coins.length === 0) {
    return null;
  }

  const counts = countCoins(coins);
  let entries = [...Object.entries(counts)].sort((a, b) => b[1] - a[1]);

  while (entries.length > 0) {
    const maxCount = entries[0][1];
    const tiedAtMax = entries.filter(([, count]) => count === maxCount);

    if (tiedAtMax.length === 1) {
      return entries[0][0] as Character;
    }

    entries = entries.filter(([, count]) => count < maxCount);
  }

  return null;
}

export function resolveZone(
  zone: Zone,
  coins: readonly Character[],
): ZoneResolution {
  const coinCounts = countCoins(coins);
  const winner = findZoneWinner(coins);
  return {
    zone,
    winner,
    moveDistance: winner !== null ? zone : 0,
    coinCounts,
  };
}

export function resolveAllZones(
  zones: Readonly<Record<Zone, readonly Character[]>>,
): readonly ZoneResolution[] {
  return [
    resolveZone(1, zones[1]),
    resolveZone(2, zones[2]),
    resolveZone(4, zones[4]),
  ];
}
